/**
 * Utility functions for handling "folder:" keyword prefixes in HTML Newsletter templates.
 * These utilities enable a "faux folders" feature where templates can be categorized
 * by adding keywords like "folder: Marketing" or "folder: Quarterly Updates".
 */

export const FOLDER_PREFIX = 'folder:';
export const FOLDER_PREFIX_WITHOUT_COLON = 'folder';

export interface FolderInfo {
	/** Display name (e.g., "Marketing") */
	name: string;
	/** Full keyword (e.g., "folder: Marketing") */
	keyword: string;
}

/**
 * Extracts folder name from a keyword string.
 * Returns null if not a folder keyword.
 * Trims whitespace from the folder name.
 * @example extractFolderName("folder: Marketing") // "Marketing"
 * @example extractFolderName("folder:Marketing") // "Marketing"
 * @example extractFolderName("other-keyword") // null
 */
export const extractFolderName = (keyword: string): string | null => {
	if (keyword.toLowerCase().startsWith(FOLDER_PREFIX.toLowerCase())) {
		const folderName = keyword.substring(FOLDER_PREFIX.length).trim();
		return folderName || null;
	}
	return null;
};

/**
 * Checks if a keyword is a stored folder keyword (has "folder:" prefix).
 */
export const isFolderKeyword = (keyword: string): boolean => {
	return keyword.toLowerCase().startsWith(FOLDER_PREFIX.toLowerCase());
};

/**
 * Checks if user input indicates they're typing a folder.
 * Triggers when they type "folder" (with or without colon).
 * Used to show "Add New Folder:" UI.
 */
export const isTypingFolderKeyword = (input: string): boolean => {
	const trimmed = input.trim().toLowerCase();
	return trimmed.startsWith(FOLDER_PREFIX_WITHOUT_COLON.toLowerCase());
};

/**
 * Extracts the folder name from user input when they're typing a folder.
 * Handles various input formats: "folder name", "folder: name", "folder:name"
 * @example extractTypedFolderName("folder September") // "September"
 * @example extractTypedFolderName("folder: September") // "September"
 * @example extractTypedFolderName("folder:September") // "September"
 */
export const extractTypedFolderName = (input: string): string | null => {
	const trimmed = input.trim();
	if (!isTypingFolderKeyword(trimmed)) {
		return null;
	}

	// Remove "folder" prefix (case insensitive)
	let remaining = trimmed.substring(FOLDER_PREFIX_WITHOUT_COLON.length);

	// Remove optional colon and any whitespace
	remaining = remaining.replace(/^[:\s]+/, '').trim();

	return remaining || null;
};

/**
 * Formats a folder name into a properly formatted folder keyword.
 * @example formatFolderKeyword("September") // "folder: September"
 * @example formatFolderKeyword("  Marketing  ") // "folder: Marketing"
 */
export const formatFolderKeyword = (folderName: string): string => {
	return `folder: ${folderName.trim()}`;
};

/**
 * Takes user input that starts with "folder" and converts it to a proper folder keyword.
 * @example formatTypedFolderInput("folder September") // "folder: September"
 * @example formatTypedFolderInput("folder: September") // "folder: September"
 */
export const formatTypedFolderInput = (input: string): string | null => {
	const folderName = extractTypedFolderName(input);
	if (!folderName) {
		return null;
	}
	return formatFolderKeyword(folderName);
};

/**
 * Gets all folder keywords from a template's keywords array.
 */
export const getTemplateFolderKeywords = (keywords?: string[]): string[] => {
	if (!keywords) return [];
	return keywords.filter(isFolderKeyword);
};

/**
 * Extracts unique folder names from an array of keywords.
 * Returns alphabetically sorted list of FolderInfo objects.
 * Handles case-insensitive deduplication while preserving original display case.
 */
export const extractFolderNamesFromKeywords = (keywords: string[]): FolderInfo[] => {
	const folderMap = new Map<string, FolderInfo>();

	keywords.forEach(keyword => {
		const folderName = extractFolderName(keyword);
		if (folderName) {
			// Use lowercase for deduplication key, preserve original case for display
			const key = folderName.toLowerCase();
			if (!folderMap.has(key)) {
				folderMap.set(key, { name: folderName, keyword });
			}
		}
	});

	return Array.from(folderMap.values()).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
};

/**
 * Checks if a template belongs to a specific folder.
 * Matching is case-insensitive.
 */
export const templateBelongsToFolder = (templateKeywords: string[] | undefined, folderName: string): boolean => {
	if (!templateKeywords) return false;
	return templateKeywords.some(keyword => {
		const name = extractFolderName(keyword);
		return name?.toLowerCase() === folderName.toLowerCase();
	});
};

/**
 * Checks if a template has ANY folder keyword.
 */
export const templateHasFolder = (templateKeywords?: string[]): boolean => {
	if (!templateKeywords) return false;
	return templateKeywords.some(isFolderKeyword);
};

/**
 * Filters templates by folder selection.
 * - null (All): shows all templates
 * - Specific folder: shows only templates with that folder keyword
 */
export const filterTemplatesByFolder = <T extends { keywords?: string[] }>(
	templates: T[],
	selectedFolder: string | null
): T[] => {
	if (!selectedFolder) {
		return templates; // "All" tab - show everything
	}

	return templates.filter(template => templateBelongsToFolder(template.keywords, selectedFolder));
};
